import { MemoryState } from './index';

const STORAGE_PREFIX = 'memory_state_';

// Función para cargar un preset desde los archivos JSON predefinidos
export async function loadPreset(userId: string): Promise<MemoryState> {
  try {
    const response = await fetch(`/presets/${userId}.json`);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const preset = await response.json();
    return {
      ...preset,
      lastInteraction: new Date().toISOString()
    };
  } catch (error) {
    console.warn(`Error loading preset for user ${userId}, using empty state`, error);
    return {
      lastInteraction: new Date().toISOString()
    };
  }
}

// Función para guardar el estado en localStorage
export function saveMemoryState(userId: string, state: MemoryState): void {
  try {
    const currentState = loadMemoryStateFromStorage(userId);
    const newState = {
      ...currentState,
      ...state,
      lastInteraction: new Date().toISOString()
    };
    localStorage.setItem(STORAGE_PREFIX + userId, JSON.stringify(newState));
  } catch (error) {
    console.error(`Error saving memory state for user ${userId}`, error);
  }
}

// Función para cargar el estado desde localStorage
function loadMemoryStateFromStorage(userId: string): MemoryState | null {
  try {
    const savedState = localStorage.getItem(STORAGE_PREFIX + userId);
    if (savedState) {
      return JSON.parse(savedState);
    }
  } catch (error) {
    console.warn(`Error loading saved state for user ${userId}`, error);
  }
  return null;
}

// Función para cargar el estado desde localStorage o preset
export async function loadMemoryState(userId: string): Promise<MemoryState> {
  const savedState = loadMemoryStateFromStorage(userId);
  if (savedState) {
    return savedState;
  }

  // Si no hay estado guardado, cargar el preset y guardarlo
  const presetState = await loadPreset(userId);
  saveMemoryState(userId, presetState);
  return presetState;
}

// Función para listar los presets disponibles
export async function listPresets(): Promise<string[]> {
  try {
    const response = await fetch('/presets/index.json');
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error listing presets', error);
    return [];
  }
}

// Función para exportar el estado actual a un archivo JSON
export function exportMemoryState(userId: string): void {
  try {
    const state = loadMemoryStateFromStorage(userId);
    if (!state) throw new Error('No state found to export');

    const blob = new Blob([JSON.stringify(state, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `memory_state_${userId}_${new Date().toISOString()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error(`Error exporting memory state for user ${userId}`, error);
    throw error;
  }
}

// Función para importar un estado desde un archivo JSON
export function importMemoryState(userId: string, file: File): Promise<MemoryState> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        if (!e.target?.result) throw new Error('Failed to read file');
        const importedState = JSON.parse(e.target.result as string);
        const currentState = loadMemoryStateFromStorage(userId) || {};
        const newState = {
          ...currentState,
          ...importedState,
          lastInteraction: new Date().toISOString()
        };
        saveMemoryState(userId, newState);
        resolve(newState);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

// Función para compartir el estado actual como un string (útil para copiar/pegar)
export function shareMemoryState(userId: string): string {
  const state = loadMemoryStateFromStorage(userId);
  if (!state) throw new Error('No state found to share');
  return JSON.stringify(state, null, 2);
}

// Función para cargar un estado desde un string compartido
export function loadSharedMemoryState(userId: string, sharedState: string): MemoryState {
  try {
    const importedState = JSON.parse(sharedState);
    const currentState = loadMemoryStateFromStorage(userId) || {};
    const newState = {
      ...currentState,
      ...importedState,
      lastInteraction: new Date().toISOString()
    };
    saveMemoryState(userId, newState);
    return newState;
  } catch (error) {
    console.error(`Error loading shared state for user ${userId}`, error);
    throw error;
  }
}
